import { Row, Col, FormGroup, Label, Input, FormFeedback, Button, Alert } from "reactstrap";
import { Field, FieldArray } from "formik";
import FeatherIcon from "feather-icons-react";
import PropTypes from "prop-types";

// Column configs for the three resident lists General Information renders. The
// Name / Email / Contact columns share widths across all three so the sections
// line up; a fourth column (Age, Relation) takes the remaining space. `md`
// widths per row must sum to 11 — the 12th column holds the add/remove buttons.
export const LEASE_HOLDER_COLUMNS = [
  { key: "name", label: "Lease Holder Name", md: 4, placeholder: "Enter name", required: true },
  { key: "email", label: "Email", md: 3, placeholder: "Enter email" },
  { key: "phone", label: "Contact", md: 4, placeholder: "Enter contact number" },
];
export const EMPTY_LEASE_HOLDER = { name: "", email: "", phone: "" };

// Occupants keep their stricter rules (name, contact and email required, numeric age).
const parseAge = (raw) => (raw === "" || raw === null || raw === undefined ? null : parseInt(raw, 10));
export const OCCUPANT_COLUMNS = [
  { key: "name", label: "Occupant Name", md: 4, placeholder: "Enter name", required: true },
  { key: "email", label: "Email", md: 3, placeholder: "Enter email", required: true },
  { key: "phone", label: "Contact", md: 2, placeholder: "Enter contact number", required: true },
  { key: "age", label: "Age", md: 2, placeholder: "Enter age", type: "number", parse: parseAge },
];
export const EMPTY_OCCUPANT = { name: "", phone: "", email: "", age: null };

export const EMERGENCY_CONTACT_COLUMNS = [
  { key: "name", label: "Emergency Contact Name", md: 4, placeholder: "Enter name", required: true },
  { key: "email", label: "Email", md: 3, placeholder: "Enter email" },
  { key: "phone", label: "Contact", md: 2, placeholder: "Enter contact number" },
  { key: "relation", label: "Relation", md: 2, placeholder: "e.g. Spouse, Parent" },
];
export const EMPTY_EMERGENCY_CONTACT = { name: "", email: "", phone: "", relation: "" };

/**
 * Generic Formik repeater for a list of contact rows under `meta.*`. Optional by
 * design: an empty list renders just an "Add" button, no N/A placeholder row.
 * The parent supplies `getNestedError` so error/touched resolution matches the
 * rest of the form.
 */
const ContactRepeater = ({ name, title, icon = "users", singular, columns, emptyRow, max = 10, values, setFieldValue, getNestedError, headerExtra = null }) => {
  const rows = Array.isArray(values) ? values : [];
  const listError = getNestedError(name);

  return (
    <div className="mb-0" data-testid={`repeater-${name}`}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Label className="meta-repeater-title mb-0">
          <FeatherIcon icon={icon} size="13" className="me-2" />
          {title}
        </Label>
        <div className="d-flex align-items-center gap-3">
          {headerExtra}
          <span className="text-muted small">{rows.length}/{max}</span>
        </div>
      </div>
      {typeof listError === "string" && (
        <Alert color="danger" className="mb-2">{listError}</Alert>
      )}
      <FieldArray name={name}>
        {({ push, remove }) => (
          <>
            {rows.map((_, index) => (
              <Row key={index} className="mb-3 align-items-end">
                {columns.map((col) => {
                  const path = `${name}[${index}].${col.key}`;
                  const err = getNestedError(path);
                  return (
                    <Col md={col.md} key={col.key}>
                      <FormGroup className="mb-0">
                        <Label htmlFor={path}>{col.label}{col.required ? " *" : ""}</Label>
                        <Field
                          name={path}
                          id={path}
                          type={col.type || "text"}
                          as={Input}
                          invalid={!!err}
                          placeholder={col.placeholder}
                          onChange={(e) => setFieldValue(path, col.parse ? col.parse(e.target.value) : e.target.value)}
                        />
                        <FormFeedback>{err}</FormFeedback>
                      </FormGroup>
                    </Col>
                  );
                })}
                <Col md={1}>
                  <div className="d-flex align-items-center justify-content-end">
                    {index === rows.length - 1 && rows.length < max && (
                      <Button color="primary" size="sm" aria-label={`Add ${singular}`} onClick={() => push({ ...emptyRow })}>
                        <FeatherIcon icon="plus" size="16" />
                      </Button>
                    )}
                    <Button color="danger" size="sm" className="ms-1" aria-label={`Remove ${singular} ${index + 1}`} onClick={() => remove(index)}>
                      <FeatherIcon icon="x" size="16" />
                    </Button>
                  </div>
                </Col>
              </Row>
            ))}
            {rows.length === 0 && (
              <div className="text-center mb-2">
                <Button color="secondary" outline aria-label={`Add ${singular}`} onClick={() => push({ ...emptyRow })}>
                  Add {singular}
                </Button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

ContactRepeater.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  singular: PropTypes.string.isRequired,
  icon: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    md: PropTypes.number.isRequired,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    type: PropTypes.string,
    parse: PropTypes.func,
  })).isRequired,
  emptyRow: PropTypes.object.isRequired,
  max: PropTypes.number,
  values: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  getNestedError: PropTypes.func.isRequired,
  headerExtra: PropTypes.node,
};

export default ContactRepeater;
